import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';
import { HttpStatusEnum } from '@core/api';
import { noGroup } from '@core/helpers';
import { CurrentUserService } from '@core/services';
import { UserGroupService } from '@core/services/user-group.service';
import { LayoutApi } from '@layout/core/api';
import { IncompleteOrder } from '@layout/core/interfaces';
import { AppRoutes } from '@shared/helpers';
import { BehaviorSubject, Subscription, filter } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class MenuCartOrdersService implements OnDestroy {
    private readonly subscriptions = new Subscription();

    readonly menuCartOrders$ = new BehaviorSubject<IncompleteOrder[] | null>(null);

    private fetchTimeout!: NodeJS.Timeout;

    constructor(
        private layoutApi: LayoutApi,
        private router: Router,
        private currentUserService: CurrentUserService,
        private userGroupService: UserGroupService
    ) {
        this.subscriptions.add(
            this.menuCartOrders$.subscribe(() => {
                this.initializeFetchingLogic();
            })
        );

        this.subscriptions.add(
            this.userGroupService.currentGroup$.subscribe(() => {
                this.getIncompleteOrders();
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private handleNavigationRefetch(event: RouterEvent) {
        let queryNavigation = false;
        if (
            event instanceof NavigationStart &&
            ((this.router.url.includes(AppRoutes.routes().ORDERS_LIST) &&
                event.url.includes(AppRoutes.routes().ORDERS_LIST)) ||
                (this.router.url.includes(AppRoutes.routes().INVENTORY_PRIMERS) &&
                    event.url.includes(AppRoutes.routes().INVENTORY_PRIMERS)) ||
                (this.router.url.includes(AppRoutes.routes().INVENTORY_SAMPLES) &&
                    event.url.includes(AppRoutes.routes().INVENTORY_SAMPLES)) ||
                (this.router.url.includes(AppRoutes.routes().ORDERS_CHECKMATE_RETEST_LIST) &&
                    event.url.includes(AppRoutes.routes().ORDERS_CHECKMATE_RETEST_LIST)))
        ) {
            queryNavigation = true;
        }

        if (event instanceof NavigationEnd && queryNavigation === false) {
            this.getIncompleteOrders();
        }
    }

    private initializeFetchingLogic() {
        clearTimeout(this.fetchTimeout);
        this.fetchTimeout = setTimeout(() => {
            this.getIncompleteOrders();
        }, 60000);
    }

    private async getIncompleteOrders() {
        clearTimeout(this.fetchTimeout);

        if (
            !this.currentUserService.getCurrentUser() ||
            !this.userGroupService.currentGroup$.value ||
            this.userGroupService.currentGroup$.value?.id === noGroup.id
        ) {
            this.initializeFetchingLogic();
            return;
        }

        const response = await this.layoutApi.getIncompleteOrders();

        if (this.menuCartOrders$.value === null) {
            this.subscriptions.add(
                this.router.events.pipe(filter(event => event instanceof RouterEvent)).subscribe(event => {
                    this.handleNavigationRefetch(event as RouterEvent);
                })
            );
        }

        if (response.data && response.status === HttpStatusEnum.SUCCESS) {
            this.menuCartOrders$.next(response.data);
        } else {
            this.menuCartOrders$.next([]);
        }
    }

    removeIncompleteOrder(orderId: number) {
        const currentOrders = this.menuCartOrders$.value;
        if (currentOrders === null) return;

        const removedOrderIndex = currentOrders.findIndex(order => order.orderId === orderId);
        if (removedOrderIndex !== undefined) {
            currentOrders.splice(removedOrderIndex, 1);
            this.menuCartOrders$.next(currentOrders);
        }
    }
}
